import { Box, Collapse, Divider, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import { SYSTEM_POPOVER_AVAILABLE_HEIGHT_VAR } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useJobs } from "../../../../hooks/useJobs";
import { MOTION, STATUS } from "../../../../theme/tokens";
import { EditDepartmentLocationConfigMode } from "../../../../utils/models";
import { useLocationCellContext } from "../Cells/LocationCellContext";
import { ApplyFromDateField } from "./ApplyFromDateField";
import { revealSx, smallLabelTypographySx, V1_SCROLL_CONTAINER_PADDING_PX, V1_TEXT_MAX_WIDTH, V1_TITLE_FONT_SIZE } from "./consts";
import { DeleteRowControl } from "./DeleteRowControl";
import { FacilityTypeField } from "./FacilityTypeField";
import { JobOrRolesField } from "./JobOrRolesField";
import { LocationEditorActions } from "./LocationEditorActions";
import { LocationEmrMappingSection } from "./LocationEmrMappingSection";
import { LocationHoursField } from "./LocationHoursField";
import { LocationIdentityFields } from "./LocationIdentityFields";
import { RatioLadderSection } from "./RatioLadderSection";
import { StaffingTargetRows } from "./StaffingTargetRows";
import { UpdateShiftsWarning } from "./UpdateShiftsWarning";
import { type EditDepartmentLocationConfigProps, useEditDepartmentLocationConfigState } from "./useEditDepartmentLocationConfigState";

/** The Schedule Template location popup. It lays out the v1 editor sections; their state lives in its hook. */
export const EditDepartmentLocationConfig: FC<EditDepartmentLocationConfigProps> = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { chosenDepartment } = useChosenDepartment();
	const enabledJobs = useJobs();
	const { weekStartDay: contextWeekStartDay } = useLocationCellContext();
	// DEFAULT: hosts outside a LocationCellProvider start the week on Sunday, as the grid does (moved verbatim).
	const dialogWeekStartDay = contextWeekStartDay ?? 0;
	const isMasterScheduleEditBlocked = chosenDepartment.disable_edit_master_schedule;
	const state = useEditDepartmentLocationConfigState(props);
	const { configSlimData, mode, handleSetConfigSlimData, handleSomeDropdownOpen, handleSomeDropdownClose, isSynergyMS, canEditRatioLadder } = state;
	const isEdit = mode === EditDepartmentLocationConfigMode.Edit;
	const modeLabel = isEdit ? t("schedule.template.v3.editor.editMode") : t("schedule.template.v3.editor.create");
	const unnamedLabel = state.existingConfig ? t("schedule.template.v3.editor.unnamed") : t("schedule.template.v3.editor.newLocation");
	const titleName = configSlimData.name ? configSlimData.name : unnamedLabel;

	return (
		<Box
			data-testid="edit-location-config"
			sx={{
				display: "flex",
				p: `${V1_SCROLL_CONTAINER_PADDING_PX}px`,
				flexDirection: "column",
				gap: 2,
				justifyContent: "start",
				alignItems: "start",
				width: "fit-content",
				// 80vh normally, but never more than a host popover says it has room for — a
				// viewport-relative cap inside a shorter popover scrolls this form inside a box taller
				// than its container, leaving Save past the clip edge and unreachable. The fallback
				// keeps the plain 80vh wherever the variable isn't set.
				maxHeight: `min(80vh, var(${SYSTEM_POPOVER_AVAILABLE_HEIGHT_VAR}, 80vh))`,
				overflow: "auto",
				zIndex: 1,
			}}
		>
			<Typography
				style={{
					fontWeight: 600,
					fontStyle: "normal",
					fontSize: V1_TITLE_FONT_SIZE,
					lineHeight: "100%",
					letterSpacing: "0%",
					color: theme.palette.primary.main,
					maxWidth: V1_TEXT_MAX_WIDTH,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{modeLabel} - {titleName}
			</Typography>
			<LocationIdentityFields
				design="v1"
				isSynergyMS={isSynergyMS}
				sectionOptions={state.uniqueLocationsOptions}
				selectedSection={state.selectedSectionOption}
				onSectionChange={state.ondDropdownOptionSelect}
				onAddSection={state.addSectionOption}
				showNameField={!props.hideNameField}
				nameOptions={state.locationNameOptions}
				name={configSlimData.name}
				onNameChange={state.handleSelectLocationName}
				onDropdownOpen={handleSomeDropdownOpen}
				onDropdownClose={handleSomeDropdownClose}
			/>
			<Divider flexItem />
			{/* EMR census area: unit / floor / rooms, or the whole department */}
			{state.emrOptionsAvailable && (
				<LocationEmrMappingSection
					design="v1"
					availableUnits={state.availableUnits}
					availableFloors={state.availableFloors}
					availableRooms={state.availableRooms}
					mapping={state.offeredMapping}
					departmentWideCensus={state.departmentWideCensus}
					onDepartmentWideCensusChange={state.handleDepartmentWideCensusChange}
					lockAreasWhenDepartmentWide={canEditRatioLadder}
					onMappingChange={state.handleEmrMappingChange}
					onDropdownOpen={handleSomeDropdownOpen}
					onDropdownClose={handleSomeDropdownClose}
				/>
			)}
			{state.roomsNotice === "rooms_loading" && (
				<Typography role="status" data-testid="location-emr-rooms-loading" sx={{ ...smallLabelTypographySx, maxWidth: V1_TEXT_MAX_WIDTH }}>
					{t("schedule.template.census.scope.rooms.loading")}
				</Typography>
			)}
			{state.roomsNotice === "rooms_failed" && (
				<SystemBanner
					tone="error"
					title={t("locations.emrEdit.roomsError.title")}
					description={t("schedule.template.census.scope.rooms.failed")}
					dataTestId="location-emr-rooms-error"
				/>
			)}
			{/* Facility type (Synergy MS only) */}
			{isSynergyMS && (
				<>
					<FacilityTypeField
						design="v1"
						departmentType={configSlimData.departmentType}
						onDepartmentTypeChange={(departmentType) => handleSetConfigSlimData({ departmentType })}
						onDropdownOpen={handleSomeDropdownOpen}
						onDropdownClose={handleSomeDropdownClose}
					/>
					<Divider flexItem />
				</>
			)}
			{/* Roles/Jobs + Set Hours (non-synergy) */}
			{!isSynergyMS && (
				<>
					<Box
						ref={state.editTimePopoverParentRef}
						sx={{
							display: "flex",
							gap: 2,
							width: "100%",
							alignItems: "start",
							zIndex: 2,
						}}
					>
						<JobOrRolesField
							design="v1"
							jobManagementEnabled={state.jobManagementEnabled}
							jobs={enabledJobs}
							selectedJobId={configSlimData.jobId}
							onJobChange={state.handleSelectedJobChanged}
							roles={configSlimData.roles}
							onRolesChange={state.handleSelectedRoleFiltersChanged}
							onDropdownOpen={handleSomeDropdownOpen}
							onDropdownClose={handleSomeDropdownClose}
						/>
						<LocationHoursField
							design="v1"
							times={state.times}
							open={state.editTimeOpen}
							setOpen={state.setEditTimeOpen}
							popoverParentRef={state.editTimePopoverParentRef}
							onTimesChange={handleSetConfigSlimData}
						/>
					</Box>
					<Divider flexItem />
				</>
			)}
			{/* Staffing targets — PPD/HPPD departments. Ratio departments are staffed by the ladder alone. */}
			{!canEditRatioLadder && (
				<>
					<StaffingTargetRows
						design="v1"
						rows={configSlimData.dayAmountRows}
						disabledDaysByRow={state.disabledDaysPerRow}
						rowHasOccupiedDays={state.rowHasOccupiedDays}
						weekStartDay={dialogWeekStartDay}
						onUpdateRow={state.handleUpdateRow}
						onRemoveRow={state.handleRemoveRow}
						onAddRow={state.handleAddRow}
					/>
					<Divider flexItem />
				</>
			)}
			{/* Ratio Ladder — the only staffing input in a ratio department; it must cover census 0–∞ */}
			{canEditRatioLadder && (
				<>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", alignItems: "start" }}>
						<Typography sx={smallLabelTypographySx}>{t("schedule.template.ratio.ladder")}</Typography>
						<RatioLadderSection
							design="v1"
							ratioLadder={configSlimData.ratioLadder}
							onRatioLadderChange={(ratioLadder) => handleSetConfigSlimData({ ratioLadder })}
							locationRoles={state.locationRoles}
							locationJobId={state.locationJobId}
							jobManagementEnabled={state.jobManagementEnabled}
						/>
						{state.ratioLadderGapText && (
							<Typography
								role="alert"
								data-testid="ratio-ladder-gap-message"
								sx={{ ...smallLabelTypographySx, color: STATUS.ERROR.main, maxWidth: V1_TEXT_MAX_WIDTH }}
							>
								{state.ratioLadderGapText}
							</Typography>
						)}
					</Box>
					<Divider flexItem />
				</>
			)}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					width: "100%",
					alignItems: "start",
				}}
			>
				<SystemSwitch
					checked={configSlimData.cantMove}
					onChange={(event) => handleSetConfigSlimData({ cantMove: event.target.checked })}
					label={t("schedule.template.cant.move")}
					dataTestId="location-cant-move-toggle"
				/>
				<Typography sx={{ ...smallLabelTypographySx, maxWidth: V1_TEXT_MAX_WIDTH }}>{t("schedule.template.cant.move.helper")}</Typography>
			</Box>
			<Divider flexItem />
			{/*
			 * unmountOnExit keeps a shut Collapse from claiming the parent's flex `gap`, and the
			 * explicit width stops it collapsing to content width under `alignItems: "start"`.
			 */}
			<Collapse in={state.willUpdateExistingShifts} timeout={MOTION.DURATION.ENTRANCE_MD} unmountOnExit sx={revealSx}>
				<UpdateShiftsWarning changes={state.shiftMatchChanges} />
			</Collapse>
			{/* Set Affect Date — on edit, and on an out-of-config create that will move real shifts.
			    Collapse defaults to appear=false, so edit mode renders it open without animating. */}
			<Collapse in={isEdit || state.willUpdateExistingShifts} timeout={MOTION.DURATION.ENTRANCE_MD} unmountOnExit sx={revealSx}>
				<ApplyFromDateField
					design="v1"
					updateFromDate={state.updateFromDate}
					onUpdateFromDateChange={state.handleUpdateFromDate}
					affectsShifts={state.applyFromDateAffectsShifts}
					datePickerAnchorEl={state.datePickerAnchorEl}
					onDatePickerAnchorElChange={state.setDatePickerAnchorEl}
				/>
				<Divider flexItem />
			</Collapse>
			{/* Delete location */}
			{isEdit && (
				<DeleteRowControl
					design="v1"
					confirming={state.confirmDelete}
					onConfirmingChange={state.setConfirmDelete}
					onDelete={state.handleDeleteLocation}
					deleting={state.deleteLoading}
					canDelete={state.allowDelete}
					editBlocked={isMasterScheduleEditBlocked}
				/>
			)}
			<LocationEditorActions
				design="v1"
				mode={mode}
				onCancel={state.onCancel}
				onSubmit={mode === EditDepartmentLocationConfigMode.Create ? state.handleCreateWithDayAmounts : state.handleSaveWithDayAmounts}
				cancelDisabled={state.updateDepartmentLocationsLoading || state.deleteLoading}
				submitDisabled={!state.isSavedEnabled || state.deleteLoading || state.confirmDelete || isMasterScheduleEditBlocked}
				submitting={state.updateDepartmentLocationsLoading}
				submitTooltip={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : !state.isSavedEnabled ? state.saveDisabledReason : undefined}
			/>
		</Box>
	);
};
